import classNames from 'classnames';
import { throttle } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { validateRequired } from 'utils/validate';
import FieldRegister from 'views/common/FieldRegister';
import TextField from 'views/common/TextField';
import { ReactComponent as SettingIcon } from 'assets/svg/setting.svg';
import {
  ExpandOutlined,
  CompressOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import ReactCodeMirror from '@uiw/react-codemirror';
import {
  javascript,
  javascriptLanguage,
  esLint,
} from '@codemirror/lang-javascript';
import * as eslint from 'eslint-linter-browserify';
import { linter, lintGutter } from '@codemirror/lint';
import { getTheme, isLightTheme, THEME } from 'utils/editorTheme';
import { format, formatWithCursor } from 'prettier';
import babelParser from 'prettier/parser-babel';
import { Button, Dropdown, notification, Tooltip } from 'antd';

const post = `var result = ApiPlugin.Post({
    url: "REPLACE_BY_URL",
    body: "REPLACE_BY_BODY_STRING",
    headers : {
      HEADER_KEY : "HEADER_VALUE"
    }
  });`;

const get = `var result = ApiPlugin.Get({
    url: "REPLACE_BY_URL",
    headers : {
      HEADER_KEY : "HEADER_VALUE"
    }
  });`;

function myCompletions(context) {
  let word = context.matchBefore(/\w*/);
  if (word.from === word.to && !context.explicit) return null;
  return {
    from: word.from,
    options: [
      { label: 'ApiPlugin.Post', type: 'text', apply: post, detail: 'macro' },
      { label: 'ApiPlugin.Get', type: 'text', apply: get, detail: 'macro' },
    ],
  };
}

const completeCustom = javascriptLanguage.data.of({
  autocomplete: myCompletions,
});

const itemsDropdown = [
  {
    key: THEME.FULLSCREEN,
    label: 'Full Screen',
    icon: <ExpandOutlined />,
  },
  {
    key: THEME.FULLSCREEN,
    label: 'Full Screen Exit',
    icon: <CompressOutlined />,
  },
  {
    key: '2',
    label: 'Themes',
    icon: <AppstoreOutlined />,
    children: [
      {
        key: THEME.VSCODE_THEME,
        label: 'VS Code',
      },
      {
        key: THEME.DRACULA,
        label: 'Dracula',
      },
      {
        key: THEME.DARCULA,
        label: 'Darcula',
      },
      {
        key: THEME.ECLIPSE,
        label: 'Eclipse',
      },
      {
        key: THEME.GRUVBOX_DARK,
        label: 'Gruvbox Dark',
      },
      {
        key: THEME.GRUVBOX_LIGHT,
        label: 'Gruvbox Light',
      },
    ],
  },
];

const IDECode = ({
  onChangeTheme,
  onChange,
  theme,
  codeData,
  isPageApprove,
  permissionDisabled,
  disabledAction,
}) => {
  const editorRef = useRef();
  const codeRefBody = useRef();
  const [height, setHeight] = useState('200px');
  const [errorFormat, setErrorFormat] = useState();
  const [fullscreen, setFullScreen] = useState(false);

  // eslint-disable-next-line
  const func = useCallback(
    throttle(() => {
      setHeight(codeRefBody.current.clientHeight + 'px');
    }, 100),
    []
  );

  const onChangeWrap = useCallback((value, viewUpdate) => {
    onChange(value);
    setErrorFormat();
    // eslint-disable-next-line
  }, []);

  const onClickFormat = () => {
    const { from, to } = editorRef.current.view.state.selection.ranges?.[0];
    try {
      if (from === to) {
        const formatted = format(codeData, {
          parser: 'babel',
          plugins: [babelParser],
          trailingComma: 'es5',
        });

        return onChange(formatted);
      }

      const { formatted } = formatWithCursor(codeData, {
        cursorOffset: 0,
        rangeEnd: to,
        rangeStart: from,
        parser: 'babel',
        trailingComma: 'es5',
        plugins: [babelParser],
      });

      onChange(formatted);
    } catch (e) {
      const { message } = e;
      setErrorFormat(message);
      notification.error({
        message: 'Format Document',
        description: 'Error format',
      });
    }
  };

  const onChangeFullScreen = () => {
    setFullScreen((f) => !f);
    setTimeout(() => func(), 0);
  };

  const onClickDropdownSettingIDE = (event) => {
    if (event.key === THEME.FULLSCREEN) {
      return onChangeFullScreen();
    }

    onChangeTheme(event.key);
  };

  const onKeyDownEditor = (e) => {
    if (e.altKey && e.ctrlKey && e.code === 'KeyL') {
      onClickFormat();
    }
  };

  useEffect(() => {
    window.addEventListener('resize', func);
    setTimeout(() => {
      func();
    });

    return () => {
      window.removeEventListener('resize', func);
    }; // eslint-disable-next-line
  }, []);

  return (
    <>
      <FieldRegister
        value={codeData}
        onChange={onChangeWrap}
        validate={validateRequired('Code')}
        height={height}
        name="function"
        disabled={isPageApprove || permissionDisabled}
        fullscreen={fullscreen}
        theme={theme}
      >
        {({
          onBlur,
          onChange,
          value,
          error,
          disabled,
          height,
          fullscreen,
          theme,
        }) => (
          <TextField
            label="Function"
            required
            help={
              <span>
                Code will be run on the worker. Ctrl + Alt + L to format
                document
              </span>
            }
            helperText={error}
          >
            <div
              onKeyDown={onKeyDownEditor}
              className={classNames('code-editor border-wraper-input', {
                'code-editor--fullscreen': fullscreen,
              })}
            >
              <div style={{ height: '100%' }} ref={codeRefBody}>
                <ReactCodeMirror
                  ref={editorRef}
                  value={value}
                  height={height}
                  extensions={[
                    javascript({ jsx: false }),
                    completeCustom,
                    lintGutter(),
                    linter(esLint(new eslint.Linter(), {})),
                  ]}
                  onChange={onChange}
                  theme={getTheme(theme)}
                  onBlur={onBlur}
                  editable={!disabled}
                />
              </div>
              {!disabledAction && (
                <Dropdown
                  arrow
                  trigger="click"
                  menu={{
                    items: itemsDropdown.filter(
                      (i, index) =>
                        (fullscreen && index !== 0) ||
                        (!fullscreen && index !== 1)
                    ),
                    onClick: onClickDropdownSettingIDE,
                  }}
                  placement="bottomRight"
                  rootClassName="dropdown-setting-ide"
                >
                  <div
                    className={classNames('scale', {
                      'scale--light': isLightTheme(theme),
                    })}
                  >
                    <SettingIcon />
                  </div>
                </Dropdown>
              )}
            </div>
          </TextField>
        )}
      </FieldRegister>
      {!disabledAction && (
        <div className="reformat">
          <Tooltip title="Ctrl + Alt + L">
            <Button onClick={onClickFormat} type="primary">
              Format Document
            </Button>
          </Tooltip>
        </div>
      )}
      {errorFormat && (
        <div className="error-format">
          <textarea disabled rows={10} value={errorFormat}></textarea>
        </div>
      )}
    </>
  );
};

export default IDECode;
